import React from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  Card,
  CardContent,
} from '@mui/material';

const PrivacyPolicy: React.FC = () => {
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper elevation={2} sx={{ p: 4 }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h3" component="h1" gutterBottom>
            Privacy Policy
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            SafetyCheckNow Mobile Application
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Last Updated: {new Date().toLocaleDateString()}
          </Typography>
        </Box>

        <Card variant="outlined" sx={{ mb: 4, backgroundColor: '#f8f9fa' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Our Commitment to Your Privacy
            </Typography>
            <Typography variant="body1">
              SafetyCheckNow is committed to protecting your privacy and ensuring the security of your personal information. 
              This privacy policy explains how we collect, use, store, and protect your information when you use our mobile 
              application for workplace safety compliance and asset management.
            </Typography>
          </CardContent>
        </Card>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" component="h2" gutterBottom>
            1. Information We Collect
          </Typography>
          
          <Typography variant="h6" component="h3" sx={{ mt: 3, mb: 2 }}>
            Personal Information
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="Account Information"
                secondary="Name, email address, employee ID, job title, and contact information for account creation and identification"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Authentication Data"
                secondary="Login credentials, biometric data (Face ID/Touch ID) stored locally on your device for secure access"
              />
            </ListItem>
          </List>

          <Typography variant="h6" component="h3" sx={{ mt: 3, mb: 2 }}>
            Location Information
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="GPS Coordinates"
                secondary="Your precise location when performing safety checks, clocking in/out, or registering assets for compliance tracking and audit purposes"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Work Location Data"
                secondary="Location of safety inspections, asset locations, and workplace check-ins to ensure compliance with safety regulations"
              />
            </ListItem>
          </List>

          <Typography variant="h6" component="h3" sx={{ mt: 3, mb: 2 }}>
            Safety and Compliance Data
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="Safety Check Records"
                secondary="Safety inspection results, equipment check data, incident reports, and compliance documentation"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Asset Information"
                secondary="Equipment photos, asset condition reports, maintenance records, and tracking data for safety compliance"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Time Tracking Data"
                secondary="Work hours, shift patterns, and time entry records for safety compliance and regulatory requirements"
              />
            </ListItem>
          </List>

          <Typography variant="h6" component="h3" sx={{ mt: 3, mb: 2 }}>
            Technical Information
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="Device Information"
                secondary="Device type, operating system, app version, and unique device identifiers for technical support and app functionality"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Usage Analytics"
                secondary="App usage patterns, feature utilization, and performance data to improve app functionality and user experience"
              />
            </ListItem>
          </List>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" component="h2" gutterBottom>
            2. Legal Basis for Processing
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 2 }}>
            Under UK GDPR, we must have a lawful basis for processing your personal data. Our legal bases include:
          </Typography>

          <List>
            <ListItem>
              <ListItemText 
                primary="Legitimate Interests"
                secondary="For workplace safety compliance, asset management, and security monitoring - these are necessary for health and safety in the workplace"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Contract"
                secondary="To fulfill our contractual obligations to provide safety management services to your employer"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Legal Obligation"
                secondary="To comply with health and safety regulations, employment law, and other legal requirements"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Consent"
                secondary="Where we have obtained your explicit consent for specific processing activities (you can withdraw consent at any time)"
              />
            </ListItem>
          </List>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" component="h2" gutterBottom>
            3. How We Use Your Information
          </Typography>
          
          <Typography variant="h6" component="h3" sx={{ mt: 3, mb: 2 }}>
            Safety Compliance and Regulatory Requirements
          </Typography>
          <List>
            <ListItem>
              <ListItemText primary="Recording and tracking safety inspections and equipment checks as required by workplace safety regulations" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Maintaining audit trails for regulatory compliance and safety documentation" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Generating compliance reports for safety authorities and regulatory bodies" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Tracking work locations and time for safety compliance and worker protection" />
            </ListItem>
          </List>

          <Typography variant="h6" component="h3" sx={{ mt: 3, mb: 2 }}>
            Service Delivery and Operations
          </Typography>
          <List>
            <ListItem>
              <ListItemText primary="Providing safety check functionality and asset management services" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Sending safety notifications, alerts, and compliance reminders" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Managing user accounts and access permissions for workplace safety systems" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Providing technical support and troubleshooting assistance" />
            </ListItem>
          </List>

          <Typography variant="h6" component="h3" sx={{ mt: 3, mb: 2 }}>
            Security and Fraud Prevention
          </Typography>
          <List>
            <ListItem>
              <ListItemText primary="Verifying user identity and preventing unauthorized access to safety systems" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Detecting and preventing fraudulent safety check submissions" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Monitoring for security threats and protecting system integrity" />
            </ListItem>
          </List>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" component="h2" gutterBottom>
            4. Location Data Collection and Usage
          </Typography>
          
          <Card variant="outlined" sx={{ mb: 3, backgroundColor: '#fff3cd' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom color="warning.main">
                Important: Location Data Usage
              </Typography>
              <Typography variant="body1">
                We collect your location data only when necessary for legitimate safety compliance purposes. 
                Your location is never used for advertising or sold to third parties.
              </Typography>
            </CardContent>
          </Card>

          <Typography variant="h6" component="h3" sx={{ mt: 3, mb: 2 }}>
            When We Collect Location Data
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="During Safety Checks"
                secondary="To record where equipment inspections and safety checks are performed for compliance auditing"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Asset Registration and Management"
                secondary="To accurately track asset locations and ensure proper equipment monitoring"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Time Tracking"
                secondary="To verify work location during clock-in/clock-out for accurate time tracking and safety compliance"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Incident Reporting"
                secondary="To record the precise location of safety incidents for investigation and compliance purposes"
              />
            </ListItem>
          </List>

          <Typography variant="h6" component="h3" sx={{ mt: 3, mb: 2 }}>
            Location Data Protection
          </Typography>
          <List>
            <ListItem>
              <ListItemText primary="Location data is encrypted during transmission and storage" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Access to location data is restricted to authorized personnel only" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Location tracking can be disabled for non-essential features" />
            </ListItem>
            <ListItem>
              <ListItemText primary="You can request deletion of location data in accordance with your rights" />
            </ListItem>
          </List>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" component="h2" gutterBottom>
            4. Information Sharing and Disclosure
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 2 }}>
            We do not sell, rent, or trade your personal information. We may share your information only in the following circumstances:
          </Typography>

          <Typography variant="h6" component="h3" sx={{ mt: 3, mb: 2 }}>
            Authorized Sharing
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="With Your Employer"
                secondary="Safety compliance data and work-related information may be shared with your employer as required for workplace safety management"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Regulatory Authorities"
                secondary="Safety inspection data and compliance reports may be shared with regulatory bodies when legally required (HSE, local authorities, RIDDOR reporting)"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Service Providers"
                secondary="Trusted third-party service providers who help us deliver our services, bound by strict confidentiality agreements"
              />
            </ListItem>
          </List>

          <Typography variant="h6" component="h3" sx={{ mt: 3, mb: 2 }}>
            Legal Requirements
          </Typography>
          <List>
            <ListItem>
              <ListItemText primary="When required by law, court order, or government regulation" />
            </ListItem>
            <ListItem>
              <ListItemText primary="To protect our rights, property, or safety, or that of our users or the public" />
            </ListItem>
            <ListItem>
              <ListItemText primary="In connection with legal proceedings or investigations" />
            </ListItem>
          </List>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" component="h2" gutterBottom>
            5. Data Security and Protection
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 2 }}>
            We implement industry-standard security measures to protect your information:
          </Typography>

          <List>
            <ListItem>
              <ListItemText 
                primary="Encryption"
                secondary="All data is encrypted in transit and at rest using industry-standard encryption protocols"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Access Controls"
                secondary="Strict access controls and authentication requirements for all system access"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Regular Security Audits"
                secondary="Regular security assessments and penetration testing to identify and address vulnerabilities"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Secure Infrastructure"
                secondary="Data hosted on secure, monitored servers with regular security updates and patches"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Employee Training"
                secondary="Regular security training for all employees with access to user data"
              />
            </ListItem>
          </List>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" component="h2" gutterBottom>
            6. Data Retention
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 2 }}>
            We retain your information for as long as necessary to provide our services and comply with legal obligations:
          </Typography>

          <List>
            <ListItem>
              <ListItemText 
                primary="Safety Compliance Records"
                secondary="Retained for 7 years or as required by UK safety regulations including HSE requirements, RIDDOR reporting obligations, and employment law"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Account Information"
                secondary="Retained while your account is active and for a reasonable period thereafter for customer service purposes"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Location Data"
                secondary="Retained for the duration necessary for safety compliance purposes, typically 3-7 years depending on regulatory requirements"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Technical Data"
                secondary="Retained for operational purposes and deleted when no longer necessary for service delivery"
              />
            </ListItem>
          </List>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" component="h2" gutterBottom>
            8. Your Rights and Choices
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 2 }}>
            You have the following rights regarding your personal information:
          </Typography>

          <List>
            <ListItem>
              <ListItemText 
                primary="Access and Portability"
                secondary="Request access to your personal information and receive a copy in a portable format"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Correction"
                secondary="Request correction of inaccurate or incomplete personal information"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Deletion"
                secondary="Request deletion of your personal information, subject to legal and regulatory retention requirements"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Restriction"
                secondary="Request restriction of processing of your personal information in certain circumstances"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Location Services"
                secondary="Control location tracking through your device settings, though this may limit app functionality"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Notifications"
                secondary="Control push notifications and email communications through app settings"
              />
            </ListItem>
          </List>

          <Card variant="outlined" sx={{ mt: 3, backgroundColor: '#d4edda' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                How to Exercise Your Rights
              </Typography>
              <Typography variant="body1">
                To exercise any of these rights, please contact us at <strong>privacy@safetychecknow.com</strong> or 
                through the contact information provided below. We will respond to your request within 30 days.
              </Typography>
            </CardContent>
          </Card>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" component="h2" gutterBottom>
            9. Children's Privacy
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 2 }}>
            SafetyCheckNow is primarily designed for workplace use. We do not knowingly collect personal information 
            from children under 13 without appropriate parental consent. For users between 13-18, we require both 
            parental consent and employer authorization. If you believe we have inadvertently collected information 
            from a child without proper consent, please contact us immediately so we can delete it.
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" component="h2" gutterBottom>
            10. UK GDPR Compliance and International Data Transfers
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 2 }}>
            We comply with the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018. 
            As a UK-based data controller, we ensure all processing of personal data meets these standards.
          </Typography>

          <Typography variant="h6" component="h3" sx={{ mt: 3, mb: 2 }}>
            Your Rights Under UK GDPR
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="Right of Access"
                secondary="Obtain confirmation that your data is being processed and access to your personal data"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Right to Rectification"
                secondary="Have inaccurate personal data corrected or completed if incomplete"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Right to Erasure"
                secondary="Request deletion of your personal data in certain circumstances"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Right to Restrict Processing"
                secondary="Limit how we use your personal data in certain circumstances"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Right to Data Portability"
                secondary="Receive your personal data in a structured, machine-readable format"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Right to Object"
                secondary="Object to processing based on legitimate interests or direct marketing"
              />
            </ListItem>
          </List>

          <Typography variant="h6" component="h3" sx={{ mt: 3, mb: 2 }}>
            International Data Transfers
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Your information may be transferred to and processed in countries outside the UK. When we transfer 
            personal information internationally, we ensure appropriate safeguards are in place, including:
          </Typography>
          <List>
            <ListItem>
              <ListItemText primary="Adequacy decisions by the UK Information Commissioner's Office (ICO)" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Standard contractual clauses approved by the ICO" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Binding corporate rules and certification schemes" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Other legally recognized transfer mechanisms under UK law" />
            </ListItem>
          </List>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" component="h2" gutterBottom>
            11. Changes to This Privacy Policy
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 2 }}>
            We may update this privacy policy from time to time to reflect changes in our practices, technology, 
            legal requirements, or other factors. When we make changes, we will:
          </Typography>

          <List>
            <ListItem>
              <ListItemText primary="Update the 'Last Updated' date at the top of this policy" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Notify you through the app or by email of material changes" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Provide the opportunity to review changes before they take effect" />
            </ListItem>
          </List>

          <Typography variant="body1" sx={{ mt: 2 }}>
            Your continued use of the app after changes take effect constitutes acceptance of the updated privacy policy.
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" component="h2" gutterBottom>
            12. Contact Information
          </Typography>
          
          <Card variant="outlined" sx={{ backgroundColor: '#e3f2fd' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                SafetyCheckNow Data Protection Team
              </Typography>
              <Typography variant="body1" paragraph>
                If you have any questions, concerns, or requests regarding this privacy policy or our data practices, 
                please contact us:
              </Typography>
              
              <Typography variant="body1" component="div">
                <strong>Email:</strong> privacy@safetychecknow.com<br />
                <strong>Data Protection Officer:</strong> dpo@safetychecknow.com
              </Typography>
              
              <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
                Data Controller Information
              </Typography>
              <Typography variant="body1" component="div">
                <strong>Data Controller:</strong> SafetyCheckNow Ltd.<br />
                <strong>Registration:</strong> UK company registered in England and Wales<br />
                <strong>ICO Registration:</strong> [Registration number will be provided upon ICO registration]
              </Typography>
              
              <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
                UK Information Commissioner's Office
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                If you are not satisfied with our response to your data protection concerns, you have the right to 
                lodge a complaint with the UK Information Commissioner's Office (ICO):
              </Typography>
              <Typography variant="body1" component="div">
                <strong>Website:</strong> <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb' }}>ico.org.uk</a><br />
                <strong>Phone:</strong> 0303 123 1113<br />
                <strong>Address:</strong> Information Commissioner's Office, Wycliffe House, Water Lane, Wilmslow, Cheshire SK9 5AF
              </Typography>
              
              <Typography variant="body2" sx={{ mt: 2 }}>
                We are committed to addressing your privacy concerns promptly and effectively. 
                We will acknowledge receipt of your inquiry within 2 business days and provide a full response within 30 days.
              </Typography>
            </CardContent>
          </Card>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography variant="body2" color="text.secondary">
            This privacy policy was last updated on {new Date().toLocaleDateString()} and is effective immediately.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            SafetyCheckNow is committed to protecting your privacy and maintaining the highest standards of data protection.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Please also review our <a href="/terms" style={{ color: '#2563eb', textDecoration: 'none' }}>Terms and Conditions</a> for additional information about using SafetyCheckNow.
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default PrivacyPolicy; 